import React from 'react';
import '../../styles/MobileScreener.css';

/**
 * Accordion section component for progressive form disclosure
 * Supports both mobile (one section at a time) and desktop (multiple sections) modes
 *
 * @param {string} title - Section header text
 * @param {string} icon - Emoji icon for the section
 * @param {string} summary - Preview text shown when collapsed
 * @param {boolean} isExpanded - Whether section is currently expanded
 * @param {boolean} isDisabled - Whether section is locked (mobile only)
 * @param {boolean} isCompleted - Whether section has been filled out
 * @param {function} onToggle - Click handler for header
 * @param {boolean} isMobile - Responsive mode flag
 * @param {ReactNode} children - Section content
 */
const AccordionFormSection = ({
  title,
  icon,
  summary = '',
  isExpanded = false,
  isDisabled = false,
  isCompleted = false,
  onToggle,
  isMobile = false,
  children
}) => {
  // Determine status icon and color
  const getStatusIcon = () => {
    if (isCompleted) return '✓';
    return '○';  // Gray circle for all incomplete states (collapsed or expanded)
  };

  const getStatusColor = () => {
    if (isCompleted) return 'text-green-500';
    return 'text-gray-500';  // Gray for incomplete states
  };

  const handleClick = () => {
    if (!isDisabled && onToggle) {
      onToggle();
    }
  };

  return (
    <div className={`accordion-section ${isDisabled ? 'disabled' : ''} ${isExpanded ? 'expanded' : ''}`}>
      <div
        className="section-header"
        onClick={handleClick}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-expanded={isExpanded}
        aria-disabled={isDisabled}
      >
        <div className="section-header-left">
          <span className={`chevron ${isExpanded ? 'rotate-90' : ''}`}>
            ›
          </span>
          <span className={`status-icon ${getStatusColor()}`}>
            {getStatusIcon()}
          </span>
          <span className="section-title">{title}</span>
        </div>
        <div className="section-header-right">
          {summary && !isExpanded && (
            <span className="section-summary">{summary}</span>
          )}
        </div>
      </div>

      <div className={`section-content ${isExpanded ? 'expanded' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export default AccordionFormSection;
